import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { eur, tempoDesde, fmtData, LINE_STATES, numRef } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Layers, Sparkles, MoreVertical, Undo2, ArrowRightLeft, Send } from "lucide-react";
import { toast } from "sonner";
import { EncomendasResumo } from "@/pages/EncomendasResumo";
import { fmtDia } from "@/components/PrazoLinha";
import { OrigemPicker, OrigemTag, origemAbbr } from "@/components/FornTag";
import { PeriodoFiltro, CarregarMais } from "@/components/PeriodoFiltro";

const VIEWS = [
  { key: "ativas", label: "Ativas" },
  { key: "arquivadas", label: "Arquivadas" },
  { key: "resumo", label: "Resumo mensal" },
];

const ENC_STATES = {
  aberta: { label: "Aberta", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  enviada: { label: "Enviada", cls: "bg-indigo-100 text-indigo-800 border-indigo-200" },
  parcial: { label: "Receção parcial", cls: "bg-sky-100 text-sky-800 border-sky-200" },
  recebida: { label: "Recebida", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  arquivada: { label: "Arquivada", cls: "bg-slate-100 text-slate-600 border-slate-200" },
};
const PERIODO = { manha: "Manhã", tarde: "Tarde" };
const DONE = ["recebido", "entregue", "faturado", "cancelado"];
const prevEnc = (e) => (e.itens || []).filter((it) => !DONE.includes(it.estado_linha)).map((it) => it.data_prevista).filter(Boolean).sort()[0] || "";

export default function Encomendas() {
  const [list, setList] = useState([]);
  const [fornecedores, setFornecedores] = useState([]);
  const [moveDlg, setMoveDlg] = useState(null); // { eid, item }
  const [moveTarget, setMoveTarget] = useState("");

  const [view, setView] = useState("ativas");
  const [loadedView, setLoadedView] = useState(null);
  const [params, setParams] = useSearchParams();
  const alvo = params.get("enc");
  useEffect(() => {
    if (!alvo || loadedView !== view) { return; }
    const el = document.querySelector(`[data-testid="encomenda-${alvo}"]`);
    if (!el) {
      if (view === "ativas") { setView("arquivadas"); } else { toast.info(`Encomenda #${alvo} não encontrada nesta lista.`); setParams({}, { replace: true }); }
      return;
    }
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    el.classList.add("ring-2", "ring-indigo-400");
    setTimeout(() => { el.classList.remove("ring-2", "ring-indigo-400"); }, 2500);
    setParams({}, { replace: true });
    /* eslint-disable-next-line */
  }, [alvo, list, loadedView]);
  const [periodo, setPeriodo] = useState({ desde: "", ate: "" });
  const [hasMore, setHasMore] = useState(false);
  const PAGE = 50;

  const load = async (append = false) => {
    const arq = view === "arquivadas";
    const params = arq
      ? { incluir_arquivadas: true, estado: "arquivada", limit: PAGE, skip: append ? list.length : 0, desde: periodo.desde || undefined, ate: periodo.ate || undefined }
      : {};
    const { data } = await api.get("/encomendas", { params });
    setList(append ? [...list, ...data] : data);
    setLoadedView(view);
    setHasMore(arq && data.length === PAGE);
  };
  useEffect(() => {
    api.get("/fornecedores").then((r) => setFornecedores(r.data));
  }, []);
  useEffect(() => {
    if (view !== "resumo") load();
    /* eslint-disable-next-line */
  }, [view, periodo]);

  const setLineState = async (it, estado) => {
    try {
      await api.post(`/atendimentos/${it.atendimento_id}/linhas/${it.linha_id}/estado`, { estado });
      toast.success("Peça rececionada.");
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const setPrevista = async (eid, data_prevista) => {
    try {
      await api.put(`/encomendas/${eid}/prevista`, { data_prevista });
      toast.success("Chegada prevista atualizada.");
      load();
    } catch (err) { toast.error(apiError(err)); }
  };
  const marcarEnviada = async (e) => {
    try {
      await api.post(`/encomendas/${e.id}/marcar-enviada`);
      toast.success(`Encomenda #${e.numero} enviada — as linhas passam a "Em trânsito" e entram no Copilot.`);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const marcarChegou = async (eid) => {
    try {
      await api.post(`/encomendas/${eid}/chegou`);
      toast.success("Marcada como chegada — aparece em \"Recolher hoje\".");
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const gerarPendentes = async () => {
    try {
      const { data } = await api.post("/encomendas/gerar");
      toast.success(`${data.criadas} criadas · ${data.atualizadas} atualizadas · ${data.linhas} linhas`);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const mudarFornOrigem = async (e, fornecedor_id, origem_id) => {
    try {
      const { data } = await api.put(`/encomendas/${e.id}`, fornecedor_id === e.fornecedor_id ? { origem_id } : { fornecedor_id, origem_id });
      toast.success(data?.juntada ? `Juntada à encomenda #${data.juntada} (mesmo armazém).` : "Fornecedor / armazém alterado.");
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const removerLinha = async (eid, item, destino, encomenda_destino_id = null) => {
    try {
      const { data } = await api.post(`/encomendas/${eid}/remover-linha`, {
        linha_id: item.linha_id, destino, encomenda_destino_id,
      });
      toast.success(data.mensagem || "Linha retirada.");
      setMoveDlg(null); setMoveTarget("");
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div className="max-w-5xl mx-auto w-full min-w-0">
      <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Encomendas</h1>
          <p className="text-xs text-slate-400">Agrupadas automaticamente por fornecedor e período (manhã/tarde). As horas de fecho definem o período.</p>
        </div>
        <Button className="bg-indigo-600 hover:bg-indigo-700 gap-1.5" data-testid="btn-gerar-encomendas" onClick={gerarPendentes}>
          <Sparkles size={15} /> Gerar encomendas pendentes
        </Button>
      </div>

      <div className="inline-flex bg-slate-100 rounded-lg p-1 mb-4 gap-1" data-testid="enc-view-toggle">
        {VIEWS.map((v) => (
          <button key={v.key} onClick={() => setView(v.key)} data-testid={`enc-view-${v.key}`}
            className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${view === v.key ? "bg-white text-indigo-700 shadow-sm" : "text-slate-500 hover:text-slate-800"}`}>
            {v.label}
          </button>
        ))}
      </div>

      {view === "arquivadas" && <div className="mb-3"><PeriodoFiltro desde={periodo.desde} ate={periodo.ate} onChange={setPeriodo} testid="enc-periodo" /></div>}
      {view === "resumo" ? <EncomendasResumo /> : (
      <div className="space-y-3">
        {list.map((e) => {
          const total = (e.itens || []).reduce((s, it) => s + (it.preco_custo || 0) * (it.qtd || 1), 0);
          const est = ENC_STATES[e.estado] || ENC_STATES.aberta;
          const editavel = !["recebida", "arquivada"].includes(e.estado);
          return (
            <div key={e.id} className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden" data-testid={`encomenda-${e.numero}`}>
              <div className="bg-indigo-50 border-b border-indigo-100 px-4 sm:px-5 py-3 flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2 sm:gap-3 flex-wrap min-w-0">
                  <Layers size={16} className="text-indigo-600 shrink-0" />
                  <span className="text-lg font-bold text-slate-900">{numRef(e.numero, e.codigo)}</span>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-semibold ${est.cls}`} data-testid={`enc-estado-${e.numero}`}>{est.label}</span>
                  {e.periodo && <span className="inline-flex items-center px-2 py-0.5 rounded-full border border-slate-200 bg-slate-50 text-slate-600 text-[10px] font-medium" data-testid={`enc-periodo-${e.numero}`}>{PERIODO[e.periodo] || e.periodo}</span>}
                  {e.estado === "aberta" ? (
                    <OrigemPicker fornecedores={fornecedores} fornecedorId={e.fornecedor_id} origemId={e.origem_id}
                      onChange={(fid, oid) => mudarFornOrigem(e, fid, oid)} placeholder="Sem fornecedor"
                      className="h-7 w-32 text-xs" testid={`enc-fornecedor-select-${e.numero}`} />
                  ) : (
                    <span className="text-sm font-semibold text-slate-700">{e.fornecedor ? <OrigemTag forn={e.fornecedor} origem={e.origem} testid={`enc-fornecedor-${e.numero}`} /> : "Sem fornecedor"}</span>
                  )}
                </div>
                <div className="text-right shrink-0">
                  <div className="text-xs text-slate-500" data-testid={`enc-data-${e.numero}`}>{fmtData(e.created_at)}</div>
                  <div className="text-[10px] text-slate-400">{tempoDesde(e.created_at)}</div>
                  {e.enviada_at && (
                    <div className="text-[10px] text-indigo-700 mt-0.5" data-testid={`enc-enviada-${e.numero}`}>
                      Enviada {new Date(e.enviada_at).toLocaleString("pt-PT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}{e.enviada_por ? ` · ${e.enviada_por}` : ""}
                    </div>
                  )}
                  <div className="font-bold text-slate-800">{eur(total)} <span className="text-xs font-normal text-slate-400">custo</span></div>
                  {e.estado === "aberta" && (
                    <Button size="sm" className="h-7 mt-1 text-xs bg-indigo-600 hover:bg-indigo-700 gap-1" onClick={() => marcarEnviada(e)} data-testid={`enc-marcar-enviada-${e.numero}`}>
                      <Send size={12} /> Confirmar envio ao fornecedor
                    </Button>
                  )}
                  {["enviada", "parcial"].includes(e.estado) && (
                    <div className="flex items-center justify-end gap-1.5 mt-1 flex-wrap">
                      <label className="text-[10px] text-slate-500 flex items-center gap-1">Chegada prevista
                        <input type="date" key={prevEnc(e) || "x"} defaultValue={prevEnc(e)} onBlur={(ev) => { if (ev.target.value && ev.target.value !== prevEnc(e)) setPrevista(e.id, ev.target.value); }}
                          className="text-[11px] border border-slate-200 rounded px-1 bg-white" data-testid={`enc-prevista-${e.numero}`} />
                      </label>
                      {!e.origem?.permite_recolha ? null : e.chegou_at
                        ? <span className="text-[10px] font-semibold text-emerald-700" data-testid={`enc-chegou-badge-${e.numero}`}>Chegou · pronto a recolher</span>
                        : <Button size="sm" className="h-6 px-2 text-[10px] bg-emerald-600 hover:bg-emerald-700" onClick={() => marcarChegou(e.id)} data-testid={`enc-chegou-${e.numero}`}>Chegou, pronto a recolher</Button>}
                    </div>
                  )}
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 text-slate-500 uppercase text-[10px]">
                    <tr>
                      <th className="text-left px-4 py-1.5 font-semibold">Pedido</th>
                      <th className="text-left px-4 py-1.5 font-semibold">Cliente</th>
                      <th className="text-left px-4 py-1.5 font-semibold">Descrição</th>
                      <th className="text-left px-4 py-1.5 font-semibold">Ref.</th>
                      <th className="text-center px-4 py-1.5 font-semibold">Qtd</th>
                      <th className="text-left px-4 py-1.5 font-semibold">Estado</th>
                      <th className="text-left px-4 py-1.5 font-semibold">Prevista</th>
                      <th className="text-right px-4 py-1.5 font-semibold">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(e.itens || []).map((it, i) => {
                      const estado = it.estado_linha;
                      const concluido = DONE.includes(estado);
                      return (
                        <tr key={i} className="border-t border-slate-100" data-testid={`enc-item-${e.numero}-${i}`}>
                          <td className="px-4 py-1.5 font-medium">{numRef(it.atendimento_numero, it.atendimento_codigo)}</td>
                          <td className="px-4 py-1.5 text-slate-600 truncate max-w-[10rem]" data-testid={`enc-item-cliente-${e.numero}-${i}`}>{it.cliente || "—"}</td>
                          <td className="px-4 py-1.5">{it.descricao}</td>
                          <td className="px-4 py-1.5 font-mono text-slate-500">{it.ref_marca || "—"}</td>
                          <td className="px-4 py-1.5 text-center">{it.qtd}</td>
                          <td className="px-4 py-1.5">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-medium ${LINE_STATES[estado]?.badge || "bg-slate-100 text-slate-600 border-slate-200"}`}>
                              {LINE_STATES[estado]?.label || estado || "—"}
                            </span>
                          </td>
                          <td className="px-4 py-1.5 text-slate-600" data-testid={`enc-item-prevista-${e.numero}-${i}`}>{concluido ? "—" : it.prevista_assumida && it.data_prevista
                            ? <span className="italic text-slate-500 cursor-help" title="Assumido: dia seguinte (sem prazo definido)">{fmtDia(it.data_prevista)}*</span>
                            : (fmtDia(it.data_prevista) || "—")}</td>
                          <td className="px-4 py-1.5 text-right whitespace-nowrap">
                            {!concluido && editavel && (estado === "em_transito" || (estado === "aguarda_fornecedor" && e.estado !== "aberta")) && (
                              <Button size="sm" className="h-7 text-xs bg-teal-600 hover:bg-teal-700" data-testid={`enc-receber-${e.numero}-${i}`} onClick={() => setLineState(it, "recebido")}>Receber</Button>
                            )}
                            {!concluido && editavel && (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0 ml-1" data-testid={`enc-item-menu-${e.numero}-${i}`}><MoreVertical size={14} /></Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem data-testid={`enc-devolver-${e.numero}-${i}`} onClick={() => removerLinha(e.id, it, "pedido")}>
                                    <Undo2 size={14} className="mr-2 text-slate-500" /> Devolver ao pedido
                                  </DropdownMenuItem>
                                  <DropdownMenuItem data-testid={`enc-mover-${e.numero}-${i}`} onClick={() => { setMoveDlg({ eid: e.id, item: it }); setMoveTarget(""); }}>
                                    <ArrowRightLeft size={14} className="mr-2 text-indigo-500" /> Mover para outra encomenda
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}
        <CarregarMais visible={hasMore} onClick={() => load(true)} testid="enc-carregar-mais" />
        {!list.length && view === "arquivadas" && <div className="text-sm text-slate-400 text-center py-12 bg-white rounded-lg border border-dashed border-slate-300" data-testid="enc-arquivadas-vazio">Sem encomendas arquivadas. Ficam aqui automaticamente quando todas as linhas estão faturadas, entregues ou canceladas.</div>}
        {!list.length && view === "ativas" && <div className="text-sm text-slate-400 text-center py-12 bg-white rounded-lg border border-dashed border-slate-300">Sem encomendas. Aprove linhas num pedido (com fornecedor) — serão agrupadas automaticamente, ou use "Gerar encomendas pendentes".</div>}
      </div>
      )}

      <Dialog open={!!moveDlg} onOpenChange={(o) => !o && setMoveDlg(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Mover linha para outra encomenda</DialogTitle></DialogHeader>
          <DialogDescription className="text-xs text-slate-500">
            Escolha a encomenda (não recebida) para onde quer mover «{moveDlg?.item?.descricao}».
          </DialogDescription>
          <Select value={moveTarget} onValueChange={setMoveTarget}>
            <SelectTrigger data-testid="move-target-select"><SelectValue placeholder="Encomenda de destino" /></SelectTrigger>
            <SelectContent>
              {list.filter((e) => e.id !== moveDlg?.eid && !["recebida", "arquivada"].includes(e.estado)).map((e) => (
                <SelectItem key={e.id} value={e.id}>{numRef(e.numero, e.codigo)} · {e.fornecedor ? origemAbbr(e.fornecedor, e.origem) : "Sem fornecedor"} {e.periodo ? `(${PERIODO[e.periodo]})` : ""}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMoveDlg(null)}>Cancelar</Button>
            <Button className="bg-indigo-600 hover:bg-indigo-700" disabled={!moveTarget} data-testid="btn-confirmar-mover"
              onClick={() => removerLinha(moveDlg.eid, moveDlg.item, "encomenda", moveTarget)}>Mover</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
